/** Доска: карточки в памяти, живые обновления по WebSocket. */
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { TicketDto, TicketWatcher } from '@sync-tracker/shared';
import { api } from '@/api';
import { realtime } from '@/api/realtime';
import { useMeta } from './meta';

export const useBoard = defineStore('board', () => {
  const tickets = ref(new Map<string, TicketDto>());
  const loaded = ref(false);
  const flash = ref(new Set<string>()); // только что появившиеся — подсветить
  /** Кто сейчас смотрит карточку: ticketId → человек (значок на плитке). */
  const watchers = ref(new Map<string, TicketWatcher>());
  let wired = false;
  /** Подписчики на изменения карточек из живых обновлений (уведомления). Первичная загрузка сюда не попадает. */
  const changeListeners = new Set<(next: TicketDto, prev: TicketDto | undefined) => void>();

  async function load() {
    const [{ tickets: list }, w] = await Promise.all([api.board(), api.tickets.watchers().catch(() => ({ items: [] }))]);
    tickets.value = new Map(list.map((t) => [t.id, t]));
    watchers.value = new Map(w.items.map((x) => [x.ticketId, x.watcher]));
    loaded.value = true;
  }

  function setWatcher(ticketId: string, watcher: TicketWatcher | null) {
    const next = new Map(watchers.value);
    if (watcher) next.set(ticketId, watcher);
    else next.delete(ticketId);
    watchers.value = next;
  }

  function upsert(t: TicketDto) {
    const prev = tickets.value.get(t.id);
    const isNew = !prev;
    const next = new Map(tickets.value);
    next.set(t.id, t);
    tickets.value = next;
    if (isNew && t.state !== 'closed') {
      flash.value = new Set(flash.value).add(t.id);
      window.setTimeout(() => {
        const s = new Set(flash.value);
        s.delete(t.id);
        flash.value = s;
      }, 2500);
    }
    for (const fn of changeListeners) fn(t, prev);
  }

  function onChange(fn: (next: TicketDto, prev: TicketDto | undefined) => void) {
    changeListeners.add(fn);
    return () => changeListeners.delete(fn);
  }

  function wire() {
    if (wired) return;
    wired = true;
    realtime.on((e) => {
      if (e.type === 'ticket') upsert(e.ticket);
      else if (e.type === 'watch') setWatcher(e.ticketId, e.watcher);
      // карточку перенесли в колонку, к которой нет доступа
      else if (e.type === 'ticket_hidden' && tickets.value.has(e.id)) {
        const next = new Map(tickets.value);
        next.delete(e.id);
        tickets.value = next;
      }
    });
    // после переподключения — перечитать, могли пропустить события
    realtime.onStatus((ok) => {
      if (ok && loaded.value) void load();
    });
    // администратор поменял доступ к колонкам — набор карточек другой, перечитываем
    const meta = useMeta();
    watch(
      () => meta.allColumns.map((c) => c.key).join(','),
      (next, prev) => {
        if (prev && next !== prev && loaded.value) void load();
      },
    );
    // закрытые карточки уходят с доски через N минут — чистим раз в минуту
    window.setInterval(prune, 60_000);
  }

  function prune() {
    const minutes = useMeta().data?.general.boardRecentClosedMinutes ?? 15;
    const cutoff = Date.now() - minutes * 60_000;
    const next = new Map([...tickets.value].filter(([, t]) => t.state !== 'closed' || new Date(t.closedAt!).getTime() > cutoff));
    if (next.size !== tickets.value.size) tickets.value = next;
  }

  const open = computed(() => [...tickets.value.values()].filter((t) => t.state !== 'closed'));
  const recentlyClosed = computed(() =>
    [...tickets.value.values()]
      .filter((t) => t.state === 'closed')
      .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? '')),
  );

  return { tickets, loaded, flash, watchers, load, wire, upsert, onChange, open, recentlyClosed };
});
